import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Card, Crate, FlexCrate, Icon, Text, Link } from '@perch-labs/shrike';
import { formatDate } from '@perch-labs/warbler';
import { copyFor, routes as getRoutes } from 'config';
import { BackLink } from 'components/back-link';
import { LoadingPage } from 'components/loading';
import { ErrorPage } from 'components/errors';
import { MAX_SECONDARY_PAGE_WIDTH } from 'domain/style';
import { Page } from 'styles';
import { StatementBanner } from 'pages/billing/components/statement-banner';
import { useReactToPrint } from 'react-to-print';
import { trackClickEvent } from 'domain/tracking';
import { BillBreakdown } from './bill-breakdown';
import { Faq } from './faq';
import { TotalDue } from '../../components/total-due';
import {
  GET_STATEMENT_PAGE_DATA,
  parseStatementPageData,
} from './statement.api';
import { UtilityStatementSummary } from '../../components/utility-statement-summary';
import { PrintedStatement } from './printed-statement';
import { CenteredCard } from './statement.style';
import { PieCarousel } from './pie-carousel';
import { CommunitySolarUpdate } from './community-solar-update';
import { DelayedCredit } from './delayed-credit';
import { DualBillingOverview } from './dual-billing-overview';
import { PaymentInstructionsCard } from './payment-instructions/payment-instructions.component';
import { CsvDownloadCard } from './csv-download';

const getCopy = copyFor('billing.statement');
const getBackLinkCopy = copyFor('navigation.backLink.dashboardLocations');

interface Props {
  location?: {
    state: {
      from: string;
    };
  };
}

/*
  TODOs:
  - limit number of utility statements displayed if multiple?
  - simplify child component props (shouldn't take the whole statement)
*/

export const Statement = (props: Props) => {
  const comingFromPage = props.location?.state?.from;
  const routeParams = useParams<{ statementId: string; planId: string }>();
  const planId = Number(routeParams.planId);
  const statementId = Number(routeParams.statementId);
  const routes = getRoutes({ statementId, planId });
  const linkState = {
    from: routes.statement,
    fromLabel: getCopy('currentLocation'),
  };

  const printedStatementRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => printedStatementRef.current,
    onBeforeGetContent: () =>
      trackClickEvent('billing.billingDetails.downloadStatement'),
  });

  const pageQuery = useQuery<
    GetStatementPageDataQuery,
    GetStatementPageDataQueryVariables
  >(GET_STATEMENT_PAGE_DATA, {
    variables: { statementId },
  });

  if (pageQuery.loading) {
    return (
      <LoadingPage
        numberOfSections={2}
        dataTestId="new-statement-page-loading"
      />
    );
  } else if (pageQuery.error) {
    return <ErrorPage to={routes.billing} title={getCopy('error.title')} />;
  } else if (!pageQuery.data?.statement) {
    return null;
  }

  const data = parseStatementPageData(pageQuery.data);

  const showPieCharts =
    data.statement.lineItemSummary.totalOtherUtilityChargeCents > 0 &&
    data.statement.lineItemSummary.totalSolarChargeCents > 0 &&
    data.statement.lineItemSummary.totalSolarSavingsCents > 0;

  const showDualBilling =
    data.statement.lineItemSummary.totalDualBillingChargeCents > 0;

  const showUtilityStatementSummary = data.solarLineItems.length <= 5;

  const showBillBreakdown = data.solarLineItems.length <= 5;

  return (
    <>
      <BackLink
        to={comingFromPage || routes.billing}
        location={getBackLinkCopy(
          comingFromPage === routes.statements ? 'statements' : 'billing'
        )}
      />
      <Page
        maxWidth={MAX_SECONDARY_PAGE_WIDTH}
        data-testid="new-statement-page"
      >
        <StatementBanner statement={data.statement} margin="0 0 32px 0" />
        <Text textStyle="heading800" tag="h1" margin="0 0 16px 0">
          {getCopy('header', {
            month: formatDate(data.primaryDate, { format: 'MMMM' }),
          })}
        </Text>
        <Text color="secondary">
          {formatDate(data.primaryDate, { format: 'MMMM dd, yyyy' })}
        </Text>
        {showUtilityStatementSummary &&
          data.utilityStatements.map(us => (
            <UtilityStatementSummary
              key={us.id}
              margin="32px 0"
              gap="16px"
              periodLabel="servicePeriod"
              accountNumber={us.utilityAccount.accountNumber || '****'}
              serviceAddress={us.utilityAccount.displayServiceAddress}
              serviceEndDate={us.serviceEndDate}
              serviceStartDate={us.serviceStartDate}
            />
          ))}
        {showUtilityStatementSummary &&
          data.hostReportItems.map(item => (
            <UtilityStatementSummary
              key={item.id}
              margin="32px 0"
              gap="16px"
              periodLabel="productionPeriod"
              accountNumber={item.utilityAccount?.accountNumber || '****'}
              serviceAddress={item.utilityAccount?.displayServiceAddress ?? ''}
              serviceEndDate={item.productionEndDate ?? item.dateReadingTo}
              serviceStartDate={
                item.productionStartDate ?? item.dateReadingFrom
              }
            />
          ))}
        <CenteredCard mobileVariant="flat" margin={{ bottom: '32px' }}>
          <Card.Content>
            <TotalDue
              directPaymentToDeveloper={
                data.plan.billingSettingsDetails.directPaymentToDeveloper
              }
              paymentDate={data.paymentDate}
              paymentDescription={data.paidMethodDescription ?? undefined}
              status={data.statement.status}
              total={data.statement.total}
              totalBeforeSolarSavings={data.statement.totalBeforeSolarSavings}
              headlineSolarSavingsRate={
                data.statement.lineItemSummary.headlineSolarSavingsRate
              }
            />
            {data.statement.delayable ? (
              <Link
                to={{
                  pathname: routes.statementDelayPayment,
                  state: linkState,
                }}
                type="CTA"
                fullWidth
                margin={{ top: '32px' }}
              >
                {getCopy('links.delayPayment')}
              </Link>
            ) : null}
            {data.statement.invoiceUrl && data.statement.status !== 'paid' ? (
              <Link
                href={data.statement.invoiceUrl}
                target="_blank"
                type="CTA"
                fullWidth
                margin={{ top: data.statement.delayable ? '8px' : '32px' }}
              >
                {getCopy('links.payInvoice')}
              </Link>
            ) : null}
            {['issued', 'payment_failed'].includes(data.statement.status) &&
            data.statement.collectAutomatically ? (
              <Link
                to={{
                  pathname: routes.paymentSettings,
                  state: linkState,
                }}
                backgroundless
                type="CTA"
                fullWidth
                margin={{ top: data.statement.invoiceUrl ? '8px' : '32px' }}
              >
                {getCopy('links.updatePayment')}
              </Link>
            ) : null}
          </Card.Content>
        </CenteredCard>
        <PaymentInstructionsCard
          paymentType={data.paymentType}
          projectDetails={data.projectDetails}
        />
        {showDualBilling && (
          <DualBillingOverview
            collectAutomatically={data.statement.collectAutomatically}
            utilityDisplayName={data.primaryUtilityName}
            paymentDate={data.paymentDate}
            paysByCheck={data.plan.billingSettingsDetails.paysByCheck}
            total={data.statement.total}
            totalDualBillingUtilityCreditCents={
              data.statement.lineItemSummary.totalDualBillingUtilityCreditCents
            }
          />
        )}
        <CsvDownloadCard statementId={statementId} />
        {showBillBreakdown && (
          <BillBreakdown
            statement={data.statement}
            utilityDisplayName={data.primaryUtilityName}
          />
        )}
        {showPieCharts && (
          <Card>
            <Card.Content>
              <PieCarousel
                total={data.statement.total}
                utilityNames={data.utilityNames}
                {...data.statement.lineItemSummary}
              />
            </Card.Content>
          </Card>
        )}
        {data.delayedCreditSolarLineItems.length > 0 && <DelayedCredit />}
        {data.preCreditSolarLineItems.map(li => (
          <Card key={li.key} mobileVariant="flat" margin="32px 0">
            <Card.Content>
              <CommunitySolarUpdate
                projectName={li.vnmSolarProject?.name}
                projectStartDate={li.vnmSolarProject?.startDate}
                stateAbbreviation={
                  li.vnmSolarProject?.utility.state.abbreviation
                }
                vnmSubscriptionStatus={li.vnmSubscriptionStatus}
              />
            </Card.Content>
          </Card>
        ))}
        <Card mobileVariant="flat" margin="32px 0">
          <Card.Content>
            <Faq
              billExperiences={data.billExperiences}
              isPreCredit={data.preCreditSolarLineItems.length > 0}
              hasSolarCharge={data.hasSolarCharge}
              utilityDisplayName={data.primaryUtilityName}
            />
          </Card.Content>
        </Card>
        <Card mobileVariant="flat" margin="32px 0">
          <Card.Content>
            <div
              onClick={handlePrint}
              style={{ cursor: 'pointer' }}
              className="click-div"
            >
              <FlexCrate
                alignItems="center"
                justifyContent="space-between"
                padding="40px 0 0 0"
              >
                <Text textStyle="heading500">{getCopy('downloadButton')}</Text>
                <Icon icon="ChevronRight" />
              </FlexCrate>
            </div>
            <Crate display="none">
              <PrintedStatement
                ref={printedStatementRef}
                lineItems={data.statement.lineItemsFormatted}
                paymentDate={data.paymentDate}
                paymentMethodDescription={
                  data.paidMethodDescription ?? undefined
                }
                total={data.statement.total}
                user={data.user}
                utilityStatements={data.utilityStatements}
              />
            </Crate>
            {data.utilityStatements.map(us => (
              <ViewOriginalUtilityStatement
                key={us.id}
                pdfSource={us.pdfSource}
                utilityName={us.utilityAccount.utility.displayName}
              />
            ))}
          </Card.Content>
        </Card>
      </Page>
    </>
  );
};

const ViewOriginalUtilityStatement: React.FC<{
  pdfSource: string | null;
  utilityName: string;
}> = ({ pdfSource, utilityName }) => {
  // Uncomment this to test, since we don't have PDFs on staging
  // if (!pdfSource) {
  //   pdfSource = 'http://example.com/foo.pdf';
  // }
  if (!pdfSource) return null;

  return (
    <>
      {/* divider */}
      <Crate height="1px" backgroundColor="tertiary" margin="24px 0 24px 0" />

      <a
        href={pdfSource}
        target="_blank"
        rel="noopener"
        style={{ textDecoration: 'none' }}
      >
        <FlexCrate
          alignItems="center"
          justifyContent="space-between"
          padding="0"
        >
          <Text textStyle="heading500">
            {getCopy('viewOriginal', { utilityName })}
          </Text>
          <Icon icon="ChevronRight" />
        </FlexCrate>
      </a>
    </>
  );
};
